import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { map, Observable, of } from 'rxjs';
import { AmbassadorProgramCompanyVehicleService } from '../../../services/ambassador-program-company-vehicle.service';
import { AmbassadorProgramCompanyVehicleDTO } from '../../../models/ambassador-program-company-vehicle-dto';
import { FileService } from '../../../services/file.service';
import { Router } from '@angular/router';
import { LocalTemplatesConfig } from '../../../config/local-templates.config';
import { AmbassadorFileDTO } from '../../../models/ambassador-file-dto';
import { environment } from '../../../../environments/environment';
import { VehicleTemplateDataService } from '../../../services/vehicle-template-data.service';

interface VehicleGroup {
  vehicleType: string;
  vehicles: AmbassadorProgramCompanyVehicleDTO[];
}

interface VehicleHtmlContent {
  [vehicleId: string]: SafeHtml;
}

interface VehicleHtmlRaw {
  [vehicleId: string]: string;
}

@Component({
  selector: 'ambassador-vehicle-list',
  standalone: true,
  templateUrl: './ambassador-vehicle-list.component.html',
  imports: [CommonModule]
})

export class AmbassadorVehicleListComponent implements OnInit {

  vehicles: AmbassadorProgramCompanyVehicleDTO[] = [];
  vehicleHtmlContent: VehicleHtmlContent = {};
  vehicleHtmlRaw: VehicleHtmlRaw = {}; // Almacena el HTML original como string para extraer datos

  // Templates locales usando la configuración centralizada
  private localVehicleInfoFile: AmbassadorFileDTO = LocalTemplatesConfig.getHtmlTemplate('vehicle-info-template.html');

  constructor(
    private vehicleService: AmbassadorProgramCompanyVehicleService,
    private fileService: FileService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private vehicleTemplateDataService: VehicleTemplateDataService
  ) {
  }

  ngOnInit() {
    this.getVehicles();
  }

  getVehicles() {
    this.vehicleService.getByUser().subscribe(res => {
      this.vehicles = res;
      // Cargar el HTML de cada vehículo
      this.vehicles.forEach(vehicle => {
        if (vehicle.HtmlInfoFile) {
          this.getHtmlVehicleInfoFile(vehicle);
        }
      });
    });
  }

  getHtmlVehicleInfoFile(vehicle: AmbassadorProgramCompanyVehicleDTO) {
    let htmlVehicleInfoFile = environment.useLocalTemplates ? this.localVehicleInfoFile : vehicle.HtmlInfoFile;
    if (htmlVehicleInfoFile) {
      this.getVehicleHtml(htmlVehicleInfoFile, vehicle).subscribe(resp => {
        this.vehicleHtmlContent[vehicle.IdVehicle] = resp;
      });
    }
  }

  getVehicleHtml(file: AmbassadorFileDTO, vehicle: AmbassadorProgramCompanyVehicleDTO): Observable<SafeHtml> {
    if (file) {
      return this.fileService.getFileDataTextByUrl(file.FileUri).pipe(
        map(resp => {
          let htmlWithId = resp.replace(/&&vehicle_image&&/g, vehicle.ImageFile?.FileUri || '').replace(/&&vehicle_name&&/g, vehicle.Name || '');
          // Almacenar también el HTML original como string para poder extraer datos después
          this.vehicleHtmlRaw[vehicle.IdVehicle] = htmlWithId;
          return this.sanitizer.bypassSecurityTrustHtml(htmlWithId);
        })
      );
    }
    return of('');
  }

  get groupedVehicles(): VehicleGroup[] {
    const groups = new Map<string, AmbassadorProgramCompanyVehicleDTO[]>();

    this.vehicles.forEach(vehicle => {
      const type = vehicle.VehicleType || 'Sin tipo';
      if (!groups.has(type)) {
        groups.set(type, []);
      }
      groups.get(type)!.push(vehicle);
    });

    return Array.from(groups.entries()).map(([vehicleType, vehicles]) => ({
      vehicleType,
      vehicles
    }));
  }

  get hasMultipleTypes(): boolean {
    const uniqueTypes = new Set(this.vehicles.map(v => v.VehicleType));
    return uniqueTypes.size > 1;
  }

  getVehicleTypeIcon(vehicleType: string): SafeHtml {
    const type = vehicleType?.toLowerCase() || '';
    if (type.includes('furgoneta') || type.includes('van') || type.includes('furgón')) {
      return this.sanitizer.bypassSecurityTrustHtml('<i class="fa-solid fa-van-shuttle"></i>');
    }
    return this.sanitizer.bypassSecurityTrustHtml('<i class="fa-solid fa-car-side"></i>');
  }

  onReserve(vehicle: AmbassadorProgramCompanyVehicleDTO) {
    // Extraer y almacenar los datos del HTML del vehículo antes de navegar
    const htmlRaw = this.vehicleHtmlRaw[vehicle.IdVehicle];
    if (htmlRaw) {
      // Usar el método combinado que extrae y almacena en un solo paso
      this.vehicleTemplateDataService.extractAndSetVehicleData(vehicle.IdVehicle, htmlRaw);
    }

    // Navegar a la página de reserva
    this.router.navigate(['/ambassador-book', vehicle.IdVehicle]);
  }

}

